import axios from "axios";
import { API } from "./api";
import { useLocation, useNavigate } from "react-router";
import { CONSTANTS } from "../constants";

type Headers = {
  authorization?: string;
};

export const headers: Headers = {};

const _fetcher = axios.create({
  // baseURL: "http://localhost:3000/",
  baseURL: "https://izunefesh-pills-manager-server.onrender.com/", // prod
  withCredentials: true,
  headers: {
    "Content-Type": "application/json", // Ensure JSON is sent
    ...headers,
  },
});

_fetcher.interceptors.request.use((config) => {
  // console.log("Request Config:", config);
  return config;
});

let isRefreshing = false;
let failedQueue: {
  resolve: (value: string) => void;
  reject: (reason?: any) => void;
}[] = [];

// Function to process the failed requests queue
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });

  failedQueue = [];
};

_fetcher.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const location = useLocation();
    const navigate = useNavigate();

    if (location.pathname === CONSTANTS.ROUTES.login) {
      return Promise.reject(error);
    }

    // If the error is due to an expired access token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // Wait for the refresh process to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            headers.authorization = `Bearer ${token}`;
            originalRequest.headers.authorization = `Bearer ${token}`;
            return _fetcher(originalRequest);
          })
          .catch((err) => {
            navigate(CONSTANTS.ROUTES.login, { replace: true });
            return Promise.reject(err);
          });
      }

      isRefreshing = true;

      try {
        // Refresh the access token
        const { data } = await _fetcher.post(API.POST.REFRESH);
        const newAccessToken = data.accessToken;

        // Update the Authorization header for the failed request
        headers.authorization = `Bearer ${newAccessToken}`;
        originalRequest.headers.authorization = `Bearer ${newAccessToken}`;
        // Process the queue and resolve waiting requests
        processQueue(null, newAccessToken);
        return _fetcher(originalRequest);
      } catch (err) {
        // Process the queue with an error

        processQueue(err, null);
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

const _get = _fetcher.get;
const _post = _fetcher.post;
const _delete = _fetcher.delete;
const _put = _fetcher.put;
const _patch = _fetcher.patch;

_fetcher.get = (url: string, config?: axios.AxiosRequestConfig) => {
  const h = config?.headers || {};
  return _get(url, {
    headers: {
      ...h,
      ...headers,
    },
  });
};
_fetcher.post = <D = any>(
  url: string,
  data?: D,
  config?: axios.AxiosRequestConfig<D>
) => {
  const h = config?.headers || {};
  return _post(url, data, {
    headers: {
      ...h,
      ...headers,
    },
  });
};
_fetcher.delete = (url: string, config?: axios.AxiosRequestConfig) => {
  const h = config?.headers || {};
  return _delete(url, {
    headers: {
      ...h,
      ...headers,
    },
  });
};
_fetcher.put = <D = any>(
  url: string,
  data?: D,
  config?: axios.AxiosRequestConfig<D>
) => {
  const h = config?.headers || {};
  return _put(url, data, {
    headers: {
      ...h,
      ...headers,
    },
  });
};
_fetcher.patch = <D = any>(
  url: string,
  data?: D,
  config?: axios.AxiosRequestConfig<D>
) => {
  const h = config?.headers || {};
  return _patch(url, data, {
    headers: {
      ...h,
      ...headers,
    },
  });
};

export const fetcher = () => {
  return _fetcher;
};
